import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Column } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { OutlineWarning01Icon } from '@tempmee/react-icons';
import {
  useDeleteDefaultPaymentMethod,
  useDeletePaymentMethod,
  useGetPaymentMethods,
} from '~/domain/Payments';
import ConfirmPopup from '../../shared/ConfirmPopup';
import { addExpirationDate } from '../utils';
import { PaymentLine } from './PaymentLine';

const DESCRIPTION_NEGATIVE_BOTTOM = { marginBottom: -40 };

const LIST_CONTAINER_STYLE = {
  width: '100%',
  marginTop: 16,
  overflowY: 'auto',
  maxHeight: 300,
};

const LIST_INDENT_STYLE = { paddingLeft: 100 };

const DIVIDER_STYLE = {
  height: 1,
  backgroundColor: colors.neutral[100],
  margin: '20px 80px',
};

const DESCRIPTION_PROP_STYLE = { width: '100%', marginBottom: 80 };

const ICON_WRAPPER_STYLE = { marginBottom: 20 };

export const DeletePaymentModal = ({ paymentToDelete, onClose }) => {
  const user = useSelector((state) => state.user.selectedChildOffice ?? state.user.user);
  const { paymentMethods: allPayments } = useGetPaymentMethods();
  const deletePayment = useDeletePaymentMethod();
  const deleteDefaultPayment = useDeleteDefaultPaymentMethod();
  const isDefaultPaymentMethod = !user?.is_pre_approved && paymentToDelete?.is_selected;
  const cardList = allPayments
    ?.filter((item) => item.type === 'card')
    .map((item) => addExpirationDate(item))
    .filter((item) => item.id !== paymentToDelete?.id && !item.isExpired);
  const bankList = allPayments
    ?.filter((item) => item.type === 'bank_account')
    .filter((item) => item.id !== paymentToDelete?.id);
  const [newDefaultPaymentMethod, setNewDefaultPaymentMethod] = useState(null);

  const handleRadioButtonClick = (item) => {
    setNewDefaultPaymentMethod(item);
  };

  const handleDeleteButton = () => {
    if (isDefaultPaymentMethod) {
      deleteDefaultPayment.mutate(
        {
          oldPaymentId: paymentToDelete?.id,
          newPaymentId: newDefaultPaymentMethod?.id,
        },
      );
    } else {
      deletePayment.mutate(paymentToDelete?.id);
    }
    onClose();
  };

  return (
    <ConfirmPopup
      Icon={() => (
        <Box style={ICON_WRAPPER_STYLE}>
          <OutlineWarning01Icon
            width={80}
            height={80}
            color={colors.warning[500]}
            aria-hidden="true"
          />
        </Box>
      )}
      dataCy="delete-payment-modal"
      title={isDefaultPaymentMethod ? 'Delete Default Payment Method' : 'Delete Payment Method'}
      description={
        isDefaultPaymentMethod ? (
          <Column style={DESCRIPTION_NEGATIVE_BOTTOM}>
            To prevent disruptions requesting shifts and services paid
            <br />
            with this {paymentToDelete?.type === 'bank_account' ? 'bank account' : 'card'}, update your default payment method.
            <Box style={LIST_CONTAINER_STYLE}>
              <Column gap={3} style={LIST_INDENT_STYLE}>
                {cardList?.map((item) => (
                  <PaymentLine
                    key={item.id}
                    item={item}
                    handleRadioButtonClick={handleRadioButtonClick}
                    isSelected={newDefaultPaymentMethod?.id === item.id}
                    showActions={false}
                    showExpiration
                  />
                ))}
              </Column>
              {cardList?.length > 0 && bankList?.length > 0 && (
                <Box style={DIVIDER_STYLE} />
              )}
              <Column gap={3} style={LIST_INDENT_STYLE}>
                {bankList?.map((item) => (
                  <PaymentLine
                    key={item.id}
                    item={item}
                    handleRadioButtonClick={handleRadioButtonClick}
                    isSelected={newDefaultPaymentMethod?.id === item.id}
                    showActions={false}
                  />
                ))}
              </Column>
            </Box>
          </Column>
        ) : (
          <>
            Are you sure you want to delete your save payment
            <br />
            method ending in <b>{paymentToDelete?.last4}</b>?
          </>
        )
      }
      descriptionStyle={DESCRIPTION_PROP_STYLE}
      leftButtonAction={onClose}
      leftButtonDataCy="btn-go-back"
      leftButtonText="Go Back"
      rightButtonAction={handleDeleteButton}
      rightButtonDataCy="btn-submit"
      rightButtonDisabled={isDefaultPaymentMethod && !newDefaultPaymentMethod}
      rightButtonText={isDefaultPaymentMethod ? 'Delete & Update' : 'Delete'}
      rightButtonStyle={{
        width: 180,
        backgroundColor:
          isDefaultPaymentMethod && !newDefaultPaymentMethod
            ? colors.primary[200]
            : colors.primary[500],
      }}
      closePopup={onClose}
      closePopupDataCy="btn-close-popup"
    />
  );
};
